// stores/tabs.js
import { defineStore } from 'pinia'
import { ref, computed } from 'vue'

export const useTabsStore = defineStore('tabs', () => {
  // ---------- 状态 ----------
  // 所有标签页列表
  const tabs = ref([])
  
  // 当前激活的标签页名称
  const activeTab = ref('')
  
  // 已打开的标签页名称集合（用于快速判断）
  const tabNames = ref(new Set())

  // ---------- 计算属性 ----------
  // 当前激活的标签页数据
  const currentTab = computed(() => {
    return tabs.value.find(tab => tab.name === activeTab.value)
  })

  // 是否有标签页
  const hasTabs = computed(() => tabs.value.length > 0)

  // ---------- 方法 ----------

  /**
   * 添加标签页
   * @param {Object} tab 标签页配置
   * @param {string} tab.name 唯一标识
   * @param {string} tab.title 显示标题
   * @param {Component} tab.component 组件
   * * @param {ID} tab.ID 唯一标识
   * @param {boolean} tab.closable 是否可关闭
   * @param {Object} tab.props 传递给组件的props
   * @param {boolean} setActive 是否自动切换为激活状态（默认true）
   * @returns {boolean} 是否添加成功
   */
  const addTab = (tab) => {

    // 如果已经存在，
    if (tabNames.value.has(tab.ID)) {
      // 如果要求激活，则切换到该标签页

      return false
    }

    // 添加标签页
    tabs.value.push(tab)
    tabNames.value.add(tab.ID)
    activeTab.value = tab.ID


    return true
  }

  /**
   * 移除标签页
   * @param {string} tabName 标签页名称
   * @param {boolean} forceClose 是否强制关闭（忽略closable属性）
   */
  const removeTab = (tabName, forceClose = false) => {
    const tabIndex = tabs.value.findIndex(tab => tab.ID === tabName)
    console.log(tabIndex)
    if (tabIndex === -1) return

    const tab = tabs.value[tabIndex]
    
    // 如果不可关闭且不是强制关闭，则阻止
    if (!forceClose && tab.closable === false) {
      return
    }

    // 移除标签页
    tabs.value.splice(tabIndex, 1)
    tabNames.value.delete(tabName)

    // 如果删除的是当前激活的标签页，需要切换到相邻标签页
    if (activeTab.value === tabName) {
      if (tabs.value.length === 0) {
        // 没有标签页了，清空激活状态
        activeTab.value = ''
      } else if (tabIndex < tabs.value.length) {
        // 切换到下一个标签页
        activeTab.value = tabs.value[tabIndex].ID
      } else {
        // 切换到上一个标签页
        activeTab.value = tabs.value[tabIndex - 1].ID
      }
    }
  }

  /**
   * 切换到指定标签页
   * @param {string} tabName 标签页名称
   */
  const setActiveTab = (tabName) => {
    if (tabNames.value.has(tabName)) {
      activeTab.value = tabName
    }
  }

  /**
   * 关闭所有标签页（除不可关闭的）
   * @param {boolean} keepFirst 是否保留第一个标签页
   */
  const closeAllTabs = (keepFirst = true) => {
    if (keepFirst && tabs.value.length > 0) {
      // 保留第一个标签页
      const firstTab = tabs.value[0]
      tabs.value = [firstTab]
      tabNames.value = new Set([firstTab.name])
      activeTab.value = firstTab.name
    } else {
      // 清空所有
      tabs.value = []
      tabNames.value = new Set()
      activeTab.value = ''
    }
  }

  /**
   * 关闭其他标签页
   * @param {string} tabName 保留的标签页名称
   */
  const closeOtherTabs = (tabName) => {
    if (!tabNames.value.has(tabName)) return

    const targetTab = tabs.value.find(tab => tab.name === tabName)
    if (!targetTab) return

    tabs.value = [targetTab]
    tabNames.value = new Set([tabName])
    activeTab.value = tabName
  }

  /**
   * 关闭右侧标签页
   * @param {string} tabName 基准标签页名称
   */
  const closeRightTabs = (tabName) => {
    const index = tabs.value.findIndex(tab => tab.name === tabName)
    if (index === -1) return

    // 保留从0到index的标签页
    const keepTabs = tabs.value.slice(0, index + 1)
    tabs.value = keepTabs
    tabNames.value = new Set(keepTabs.map(tab => tab.name))
    
    // 如果当前激活的标签页不在保留列表中，切换到保留的最后一个
    if (!tabNames.value.has(activeTab.value)) {
      activeTab.value = keepTabs[keepTabs.length - 1].name
    }
  }

  /**
   * 判断标签页是否已打开
   * @param {string} tabName 标签页名称
   * @returns {boolean}
   */
  const hasTab = (tabName) => {
    return tabNames.value.has(tabName)
  }

  /**
   * 获取标签页
   * @param {string} tabName 标签页名称
   * @returns {Object|undefined}
   */
  const getTab = (tabName) => {
    return tabs.value.find(tab => tab.name === tabName)
  }

  /**
   * 更新标签页标题
   * @param {string} tabName 标签页名称
   * @param {string} title 新标题
   */
  const updateTabTitle = (tabName, title) => {
    const tab = tabs.value.find(tab => tab.name === tabName)
    if (tab) {
      tab.title = title
    }
  }

  /**
   * 更新标签页props
   * @param {string} tabName 标签页名称
   * @param {Object} props 新的props
   */
  const updateTabProps = (tabName, props) => {
    const tab = tabs.value.find(tab => tab.name === tabName)
    if (tab) {
      tab.props = { ...tab.props, ...props }
    }
  }

  // ---------- 返回所有状态和方法 ----------
  return {
    // 状态
    tabs,
    activeTab,
    tabNames,
    
    // 计算属性
    currentTab,
    hasTabs,
    
    // 方法
    addTab,
    removeTab,
    setActiveTab,
    closeAllTabs,
    closeOtherTabs,
    closeRightTabs,
    hasTab,
    getTab,
    updateTabTitle,
    updateTabProps
  }
})